
// 导入React（如果项目中还没有全局导入）
import React from 'react';
type UserInfo = {
  id?: string;
  name?: string;
  email?: string;
  [key: string]: any;
};

type StoreState = {
  token: string | null;
  userInfo: UserInfo | null;
  isAuthenticated: boolean;
};

type StoreActions = {
  setToken: (token: string) => void;
  setUserInfo: (userInfo: UserInfo) => void;
  setAuth: (token: string, userInfo: UserInfo) => void;
  clearAuth: () => void;
  getState: () => StoreState;
};

type Store = StoreState & StoreActions;

// 订阅者列表
type Subscriber = (state: StoreState) => void;
const subscribers: Subscriber[] = [];

// 初始状态
let state: StoreState = {
  token: localStorage.getItem('haier-user-center-access-token'),
  userInfo: (() => {
    const userInfoStr = localStorage.getItem('haier-user-center-user-info');
    try {
      return userInfoStr ? JSON.parse(userInfoStr) : null;
    } catch {
      return null;
    }
  })(),
  isAuthenticated: false,
};

// 初始化认证状态
state.isAuthenticated = !!(state.token && state.userInfo);

// 通知所有订阅者
const notifySubscribers = () => {
  subscribers.forEach(subscriber => subscriber(state));
};

// 更新状态的辅助函数
const updateState = (newState: Partial<StoreState>) => {
  state = { ...state, ...newState };
  notifySubscribers();
};

// Store实例
export const store: Store = {
  // 状态
  get token() { return state.token; },
  get userInfo() { return state.userInfo; },
  get isAuthenticated() { return state.isAuthenticated; },

  // 动作
  setToken: (token: string) => {
    localStorage.setItem('haier-user-center-access-token', token);
    updateState({ 
      token, 
      isAuthenticated: !!(token && state.userInfo) 
    });
  },

  setUserInfo: (userInfo: UserInfo) => {
    localStorage.setItem('haier-user-center-user-info', JSON.stringify(userInfo));
    updateState({ 
      userInfo, 
      isAuthenticated: !!(state.token && userInfo) 
    });
  },

  setAuth: (token: string, userInfo: UserInfo) => {
    localStorage.setItem('haier-user-center-access-token', token);
    localStorage.setItem('haier-user-center-user-info', JSON.stringify(userInfo));
    updateState({ 
      token, 
      userInfo, 
      isAuthenticated: true 
    });
  },

  clearAuth: () => {
    localStorage.removeItem('haier-user-center-access-token');
    localStorage.removeItem('haier-user-center-user-info');
    updateState({ 
      token: null, 
      userInfo: null, 
      isAuthenticated: false 
    });
  },

  getState: () => ({ ...state }),
};

// Hook用于在React组件中订阅状态变化
export const useStore = (selector?: (state: StoreState) => any) => {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  React.useEffect(() => {
    const subscriber = () => forceUpdate();
    subscribers.push(subscriber);
    
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index > -1) {
        subscribers.splice(index, 1);
      }
    };
  }, []);

  return selector ? selector(state) : state;
};


export default store;
